import React, { useState } from 'react';
import { User } from '../types/user';
import Button from './Button';

interface DeleteUserModalProps {
    user: User;
    taskCount: number;
    availableUsers: User[];
    onConfirm: (cascade: boolean, reassignTo?: string) => void;
    onCancel: () => void;
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
    user,
    taskCount,
    availableUsers,
    onConfirm,
    onCancel,
}) => {
    const [deleteOption, setDeleteOption] = useState<'cancel' | 'cascade' | 'reassign'>('cancel');
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);

    const handleConfirm = () => {
        if (deleteOption === 'cascade') {
            onConfirm(true);
        } else if (deleteOption === 'reassign' && selectedUserId) {
            onConfirm(false, selectedUserId);
        }
    };

    const canConfirm = 
        deleteOption === 'cascade' || 
        (deleteOption === 'reassign' && selectedUserId !== undefined);

    const displayName = user.full_name || user.username;

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h2 style={styles.title}>Delete User: {displayName}</h2>
                </div>

                {taskCount > 0 ? (
                    <div style={styles.body}>
                        <div style={styles.warning}>
                            <span style={styles.warningIcon}>⚠️</span>
                            <p style={styles.warningText}>
                                This user is assigned to <strong>{taskCount}</strong> task{taskCount !== 1 ? 's' : ''}.
                            </p>
                        </div>

                        <p style={styles.question}>What would you like to do with the assigned tasks?</p>

                        <div style={styles.options}>
                            <label style={styles.option}>
                                <input
                                    type="radio"
                                    name="deleteOption"
                                    value="cascade"
                                    checked={deleteOption === 'cascade'}
                                    onChange={() => setDeleteOption('cascade')}
                                    style={styles.radio}
                                />
                                <div style={styles.optionContent}>
                                    <div style={styles.optionTitle}>Unassign all tasks</div>
                                    <div style={styles.optionDescription}>
                                        Set all {taskCount} task{taskCount !== 1 ? 's' : ''} as unassigned before deleting this user
                                    </div>
                                </div>
                            </label>

                            <label style={styles.option}>
                                <input
                                    type="radio"
                                    name="deleteOption"
                                    value="reassign"
                                    checked={deleteOption === 'reassign'}
                                    onChange={() => setDeleteOption('reassign')}
                                    style={styles.radio}
                                />
                                <div style={styles.optionContent}>
                                    <div style={styles.optionTitle}>Reassign tasks to another user</div>
                                    <div style={styles.optionDescription}>
                                        Transfer all tasks to a different user before deleting
                                    </div>
                                    {deleteOption === 'reassign' && (
                                        <select
                                            value={selectedUserId || ''}
                                            onChange={(e) => setSelectedUserId(e.target.value)}
                                            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ marginTop: '12px' }}
                                        >
                                            <option value="">Select a user...</option>
                                            {availableUsers
                                                .filter(u => u.id !== user.id)
                                                .map(u => (
                                                    <option key={u.id} value={u.id}>
                                                        {u.full_name || u.username} (@{u.username})
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    )}
                                </div>
                            </label>

                            <label style={styles.option}>
                                <input
                                    type="radio"
                                    name="deleteOption"
                                    value="cancel"
                                    checked={deleteOption === 'cancel'}
                                    onChange={() => setDeleteOption('cancel')}
                                    style={styles.radio}
                                />
                                <div style={styles.optionContent}>
                                    <div style={styles.optionTitle}>Cancel deletion</div>
                                    <div style={styles.optionDescription}>
                                        Keep the user and their task assignments
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                ) : (
                    <div style={styles.body}>
                        <p>Are you sure you want to delete this user?</p>
                        <p style={styles.noTasksNote}>This user has no assigned tasks.</p>
                    </div>
                )}

                <div style={styles.footer}>
                    <Button onClick={onCancel} variant="outline">
                        Cancel
                    </Button>
                    {taskCount > 0 ? (
                        <Button
                            onClick={handleConfirm}
                            disabled={!canConfirm}
                            variant="danger"
                        >
                            {deleteOption === 'cascade' ? 'Unassign & Delete User' : 
                             deleteOption === 'reassign' ? 'Reassign & Delete User' : 
                             'Select an option'}
                        </Button>
                    ) : (
                        <Button onClick={() => onConfirm(false)} variant="danger">
                            Delete User
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    },
    header: {
        padding: '20px',
        borderBottom: '1px solid #ddd',
        backgroundColor: '#f9f9f9',
    },
    title: {
        margin: 0,
        color: '#333',
        fontSize: '20px',
    },
    body: {
        padding: '24px',
    },
    warning: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '6px',
        marginBottom: '20px',
    },
    warningIcon: {
        fontSize: '24px',
    },
    warningText: {
        margin: 0,
        color: '#856404',
        fontSize: '14px',
    },
    question: {
        fontSize: '16px',
        fontWeight: 'bold' as const,
        marginBottom: '16px',
        color: '#333',
    },
    options: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
    },
    option: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        border: '2px solid #ddd',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    radio: {
        marginTop: '4px',
        cursor: 'pointer',
    },
    optionContent: {
        flex: 1,
    },
    optionTitle: {
        fontWeight: 'bold' as const,
        fontSize: '15px',
        marginBottom: '4px',
        color: '#333',
    },
    optionDescription: {
        fontSize: '13px',
        color: '#666',
    },
    noTasksNote: {
        fontSize: '14px',
        color: '#666',
        fontStyle: 'italic' as const,
    },
    footer: {
        padding: '16px 20px',
        borderTop: '1px solid #ddd',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        backgroundColor: '#f9f9f9',
    },
};

export default DeleteUserModal;
